import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, flatMap, map, tap } from 'rxjs/operators';

interface TranslateModel {
  [key: string]: string | TranslateModel;
}

class MultiTranslateHttpLoader implements TranslateLoader {
  private cachedData: { [key: string]: TranslateModel | string[] } = {};

  constructor(
    private http: HttpClient,
    private mainPath: string
  ) {}

  private getData(
    url: string,
    defaultValue?: string[] | TranslateModel
  ): Observable<string[] | TranslateModel> {
    return this.cachedData[url]
      ? of(this.cachedData[url])
      : this.http.get(`${this.mainPath}${url}?rnd=${Date.now()}`).pipe(
          tap((data: string[]) => (this.cachedData[url] = data)),
          catchError(() => of(defaultValue || {}))
        );
  }

  private loadTranslationFiles(
    language: string,
    localizationFiles: string[]
  ): Observable<TranslateModel> {
    const requests = localizationFiles.map<Observable<TranslateModel>>(
      (path: string) => this.getData(`${language}/${path}.json`) as Observable<TranslateModel>
    );
    return forkJoin(requests).pipe(
      map((localizationContent: TranslateModel[]) =>
        localizationContent.reduce((firstFile: TranslateModel, nextFile: TranslateModel) =>
          Object.assign(firstFile, nextFile)
        )
      )
    );
  }

  getTranslation(language: string): Observable<TranslateModel> {
    return this.getData('schema.json', []).pipe(
      flatMap((localizationFiles: string[]) => {
        return localizationFiles.length > 0
          ? this.loadTranslationFiles(language, localizationFiles)
          : of({});
      })
    );
  }
}

export function HttpLoaderFactory(httpClient: HttpClient): MultiTranslateHttpLoader {
  const firstUrlSegment = window.location.pathname.replace(/^\/([^\/]*).*$/, '$1');
  return new MultiTranslateHttpLoader(
    httpClient,
    `${document.location.origin}/${firstUrlSegment}/assets/i18n/`
  );
}
